import { 
  fetchChildrenUsers, 
  fetchLocationUsers, 
  downloadLiveReport,
  LiveReportDownloadRequest,
  downloadSnapshotReport,
  SnapshotReportDownloadRequest
} from "./reportsDataService";
import { newReportConfig } from "../features/newMdmReports/mdmReportsUtils";
import { Dayjs } from "dayjs";
import { ChannelkartNetworkPost, ChannelkartNetworkGet } from "./networkService";
import { getLob } from "./authenticationService";
import { fetchAndDownloadReport } from "./reportService";


const convertDayjsToUtcString = (date: Dayjs, isEndDate: boolean = false): string => {
  if (!date || !date.isValid()) {
    return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  const year = date.year();
  const month = date.month();
  const day = date.date();

  if (isEndDate) {
    const utcDate = new Date(Date.UTC(year, month, day, 18, 29, 59));
    
    if (Number.isNaN(utcDate.getTime())) {
      return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
    }
    
    return utcDate.toISOString().replace(/\.\d{3}Z$/, 'Z');
  } else {
    const utcDate = new Date(Date.UTC(year, month, day, 18, 30, 0));
    
    utcDate.setDate(utcDate.getDate() - 1);
    
    if (Number.isNaN(utcDate.getTime())) {
      return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
    }

    return utcDate.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }
};

export interface DownloadParams {
  selectedReport: newReportConfig;
  filters: Record<string, string[]>;
  dateRangeType: "daterange" | "period";
  fromDate: Dayjs;
  toDate: Dayjs;
  period?: string;
  year?: string;
  format: string;
  primaryFilter: "sales" | "geographical" | "distributor" | null;
  customFilters: string[];
  salesDrillDownPath?: Array<{ level: string; value: string }>;
  geoDrillDownPath?: Array<{ level: string; value: string }>;
}

/**
 * Collect distributor codes based on primary filter selection
 * Then filter by division and type if provided
 */
export async function collectDistributorCodes(
  selectedReport: newReportConfig,
  filters: Record<string, string[]>,
  primaryFilter: "sales" | "geographical" | "distributor" | null,
  salesDrillDownPath?: Array<{ level: string; value: string }>,
  geoDrillDownPath?: Array<{ level: string; value: string }>
): Promise<string[]> {
  let distributorCodes: string[] = [];
  const distributorKey = selectedReport.distributorFilter?.field;

  // If no primary filter is selected, check for direct distributor selection
  if (!primaryFilter) {
    if (selectedReport.distributorFilter?.enabled) {
      const distributorKey = selectedReport.distributorFilter.field;
      const selectedDistributors = filters[distributorKey] || [];
      if (selectedDistributors.length > 0) {
        distributorCodes = selectedDistributors;
        return distributorCodes;
      }
    }
    const authContext = JSON.parse(localStorage.authContext || '{}');
    const loggedInUser = authContext?.user?.loginId;

    if(selectedReport.isDistributorView && loggedInUser){
      distributorCodes.push(loggedInUser);
    }
    return distributorCodes;
  }

  // Primary Filter: Distributor - Direct selection
  if (primaryFilter === "distributor") {
    if (selectedReport.distributorFilter?.enabled) {
      const distributorKey = selectedReport.distributorFilter.field;
      const selectedDistributors = filters[distributorKey] || [];
      if (selectedDistributors.length > 0) {
        distributorCodes = selectedDistributors;
        return distributorCodes;
      }
    } 
    return distributorCodes;
  }

  // Primary Filter: Sales Hierarchy
  if (primaryFilter === "sales" && selectedReport.salesHierarchyFilter?.enabled && salesDrillDownPath && salesDrillDownPath.length > 0) {
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    const supplierLevel = hierarchyOrder.length > 0 ? hierarchyOrder[hierarchyOrder.length - 1] : 'supplier';
    
    // First, check if user has directly selected suppliers
    if (filters[supplierLevel] && filters[supplierLevel].length > 0) {
      distributorCodes = filters[supplierLevel];
      return distributorCodes;
    }
    
    // If no direct supplier selection, find the deepest hierarchy level selection
    // and fetch their supplier children
    for (let i = hierarchyOrder.length - 2; i >= 0; i--) {
      const level = hierarchyOrder[i];
      const selectedUsers = filters[level];
      
      if (selectedUsers && selectedUsers.length > 0) {
        // Fetch suppliers for all selected users at this level
        const allSuppliers: string[] = [];
        for (const userId of selectedUsers) {
          const children = await fetchChildrenUsers(userId, supplierLevel);
          allSuppliers.push(...children);
        }
        
        if (allSuppliers.length > 0) {
          distributorCodes = Array.from(new Set(allSuppliers)); // Remove duplicates
          return distributorCodes;
        }
      }
    }
  }

  // Primary Filter: Geographical Hierarchy
  if (primaryFilter === "geographical" && selectedReport.geographicalHierarchyFilter?.enabled) {
    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    const selectedLevel = filters[levelKey]?.[0];
    const selectedValue = filters[selectedLevel]?.[0];
    
    if (selectedLevel && selectedValue) {
      const locationUsers = await fetchLocationUsers(selectedLevel, selectedValue, 'supplier');
      if (locationUsers.length > 0) {
        distributorCodes = locationUsers;
        return distributorCodes;
      }
    }
    
    // If no distributors found, check for direct distributor selection
    if (selectedReport.distributorFilter?.enabled) {
      const distributorKey = selectedReport.distributorFilter.field;
      const selectedDistributors = filters[distributorKey] || [];
      if (selectedDistributors.length > 0) {
        distributorCodes = selectedDistributors;
        return distributorCodes;
      }
    }
  }

  return distributorCodes;
}

/**
 * Build filters map for download payload
 * Only includes distributor_code if directly selected, plus custom filters
 */
export function buildFiltersMap(
  selectedReport: newReportConfig,
  filters: Record<string, string[]>,
  distributorCodes: string[],
  customFilters: string[]
): Record<string, string[]> {
  const filtersMap: Record<string, string[]> = {};

  if (distributorCodes.length > 0) {
    filtersMap.distributor_code = distributorCodes;
  }

  // Add custom filters
  if (filters && Object.keys(filters).length > 0) {
    const levelKeys = [
      selectedReport.salesHierarchyFilter?.levelFilterField,
      selectedReport.geographicalHierarchyFilter?.levelFilterField
    ].filter(Boolean) as string[];
    
    const hierarchyLevelNames = new Set<string>();
    if (selectedReport.salesHierarchyFilter?.hierarchyOrder) {
      selectedReport.salesHierarchyFilter.hierarchyOrder.forEach(level => {
        hierarchyLevelNames.add(level);
      });
    }

    const supplierLevel = selectedReport.salesHierarchyFilter?.hierarchyOrder?.[selectedReport.salesHierarchyFilter.hierarchyOrder.length - 1] || 'supplier';
    const distributorKey = selectedReport.distributorFilter?.field;
    const mergedFilterConfig = selectedReport.mergedFilters || {};
    const mergedValueToSource: Record<string, Record<string, string>> = {};
    Object.entries(mergedFilterConfig).forEach(([mergedAlias, sources]) => {
      mergedValueToSource[mergedAlias] = {};
      sources.forEach(source => {
        mergedValueToSource[mergedAlias][source.value] = source.alias;
      });
    });
    
    Object.entries(filters).forEach(([key, values]) => {
      if (levelKeys.includes(key)) {
        return;
      }

      if (hierarchyLevelNames.has(key)) {
        return;
      }
      if (key.endsWith("_dynamic")) {
        const mergedAlias = key.replace("_dynamic", "");
        const mergedSelection = filters[mergedAlias]?.[0];
        const sourceAlias = mergedSelection ? mergedValueToSource[mergedAlias]?.[mergedSelection] : undefined;
        if (sourceAlias && values && values.length > 0 && values[0] !== "") {
          filtersMap[sourceAlias] = values;
        }
        return;
      }
      if (mergedFilterConfig[key]) {
        return;
      }

      if(customFilters.includes(key)){
        if(values && values.length > 0 && values[0] !== ""){
          filtersMap[key] = values;
        }
      }
      
      if(key===supplierLevel || key === distributorKey){
        if(values && values.length > 0 && values[0] !== ""){
          filtersMap.distributor_code = values;
        }
      }
    });
  }

  return filtersMap;
}

/**
 * Build location filters from geo drill-down path
 */
export function buildLocationFilters(
  geoDrillDownPath: Array<{ level: string; value: string }>
): Array<{ level: string; value: string }> {
  if (!geoDrillDownPath || geoDrillDownPath.length === 0) {
    return [];
  }
  
  // Group by level and return unique level-value pairs
  const locationMap = new Map<string, Set<string>>();
  
  geoDrillDownPath.forEach(item => {
    if (!locationMap.has(item.level)) {
      locationMap.set(item.level, new Set());
    }
    locationMap.get(item.level)!.add(item.value);
  });
  
  const locationFilters: Array<{ level: string; value: string }> = [];
  locationMap.forEach((values, level) => {
    values.forEach(value => {
      locationFilters.push({ level, value });
    });
  });
  
  return locationFilters;
}

/**
 * Build user filters from sales drill-down path
 * direct: true if it's the last level in hierarchy (supplier level), false otherwise
 */
export function buildUserFilters(
  salesDrillDownPath: Array<{ level: string; value: string }>,
  hierarchyOrder: string[]
): Array<{ userId: string; direct: boolean }> {
  if (!salesDrillDownPath || salesDrillDownPath.length === 0) {
    return [];
  }
  
  const supplierLevel = hierarchyOrder.length > 0 ? hierarchyOrder[hierarchyOrder.length - 1] : 'supplier';
  
  const userFilters: Array<{ userId: string; direct: boolean }> = [];
  
  salesDrillDownPath.forEach(item => {
    // direct: true if it's the supplier level (last level), false for all other levels
    const isDirect = item.level === supplierLevel;
    userFilters.push({
      userId: item.value,
      direct: isDirect
    });
  });
  
  return userFilters;
}


export function buildDownloadPayload(
  selectedReport: newReportConfig,
  dateRangeType: "daterange" | "period",
  fromDate: Dayjs,
  toDate: Dayjs,
  period: string | undefined,
  year: string | undefined,
  format: string,
  filtersMap: Record<string, string[]>,
  salesDrillDownPath: Array<{ level: string; value: string }>,
  geoDrillDownPath: Array<{ level: string; value: string }>
): LiveReportDownloadRequest {
  const locationFilters = buildLocationFilters(geoDrillDownPath);
  const userFilters = buildUserFilters(
    salesDrillDownPath,
    selectedReport.salesHierarchyFilter?.hierarchyOrder || []
  );

  const downloadPayload: LiveReportDownloadRequest = {
    configName: selectedReport.reportName,
    format: format
  };

  // Only include distributorFilter if at least one of locationFilters or userFilters has data
  // and if there is no direct distributor selected from buildFiltersMap
  // If direct distributor selection is there, we will send that only via filtersMap
  const hasDirectDistributorSelection = filtersMap.distributor_code && filtersMap.distributor_code.length > 0;
  
  if ((locationFilters.length > 0 || userFilters.length > 0) && !hasDirectDistributorSelection) {
    downloadPayload.distributorFilter = {};
    
    // Only include locationFilters if it has data
    if (locationFilters.length > 0) {
      downloadPayload.distributorFilter.locationFilters = locationFilters;
    }

    // Only include userFilters if it has data
    if (userFilters.length > 0) {
      downloadPayload.distributorFilter.userFilters = userFilters;
    }
  }

  if (dateRangeType === "period" && period && year) {
    downloadPayload.period = period;
    downloadPayload.year = year;
  } else {
    const startDate = fromDate.format("YYYY-MM-DD");
    const endDate = toDate.format("YYYY-MM-DD");
    
    downloadPayload.dateRange = {
      startDate,
      endDate
    };
  }

  if (filtersMap && Object.keys(filtersMap).length > 0) {
    downloadPayload.filters = {
      map: filtersMap
    };
  }

  return downloadPayload;
}

export function buildSnapshotDownloadPayload(
  selectedReport: newReportConfig,
  dateRangeType: "daterange" | "period",
  fromDate: Dayjs,
  toDate: Dayjs,
  period: string | undefined,
  year: string | undefined,
  format: string,
  filtersMap: Record<string, string[]>,
  salesDrillDownPath: Array<{ level: string; value: string }>,
  geoDrillDownPath: Array<{ level: string; value: string }>
): SnapshotReportDownloadRequest {
  const locationFilters = buildLocationFilters(geoDrillDownPath);
  const userFilters = buildUserFilters(
    salesDrillDownPath,
    selectedReport.salesHierarchyFilter?.hierarchyOrder || []
  );

  const downloadPayload: SnapshotReportDownloadRequest = {
    reportName: selectedReport.reportName,
    format: format
  };

  // Only include distributorFilter if at least one of locationFilters or userFilters has data
  // and if there is no direct distributor selected from buildFiltersMap
  // If direct distributor selection is there, we will send that only via filtersMap
  const hasDirectDistributorSelection = filtersMap.distributor_code && filtersMap.distributor_code.length > 0;
  
  if ((locationFilters.length > 0 || userFilters.length > 0) && !hasDirectDistributorSelection) {
    downloadPayload.distributorFilter = {};
    
    // Only include locationFilters if it has data
    if (locationFilters.length > 0) {
      downloadPayload.distributorFilter.locationFilters = locationFilters;
    }

    // Only include userFilters if it has data
    if (userFilters.length > 0) {
      downloadPayload.distributorFilter.userFilters = userFilters;
    }
  }

  const startDate = convertDayjsToUtcString(fromDate, false);
  const endDate = convertDayjsToUtcString(toDate, true);
    
  downloadPayload.dateRange = {
      startDate,
      endDate
  };

  if (filtersMap && Object.keys(filtersMap).length > 0) {
    downloadPayload.filters = {
      map: filtersMap
    };
  }

  return downloadPayload;
}



export function triggerBrowserDownload(
  blob: Blob,
  reportName: string,
  format: string
): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${reportName}_${new Date().toISOString().split('T')[0]}.${format.toLowerCase()}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}


export async function handleMdmReportDownload(params: DownloadParams): Promise<{
  success: boolean;
  message: string;
  error?: any;
}> {
  const { 
    selectedReport, 
    filters, 
    dateRangeType, 
    fromDate, 
    toDate, 
    period, 
    year, 
    format,
    primaryFilter,
    customFilters,
    salesDrillDownPath = [],
    geoDrillDownPath = []
  } = params;

  try {
    // Validate date range
    if (dateRangeType === "daterange" && fromDate && toDate) {
      if (toDate.isBefore(fromDate)) {
        return {
          success: false,
          message: "To Date should be greater than from date."
        };
      }
    }

    const isPDFReport = format.toLowerCase() === 'pdf';
    
    if (isPDFReport) {
      return await downloadPDFReportWithStatus(
        selectedReport,
        filters,
        fromDate,
        toDate,
        primaryFilter,
        salesDrillDownPath,
        geoDrillDownPath
      );
    }

    const distributorCodes = await collectDistributorCodes(
      selectedReport, 
      filters, 
      primaryFilter,
    );

    const filtersMap = buildFiltersMap(selectedReport, filters, distributorCodes, customFilters, );
    const isLiveReport = selectedReport.isLiveReport;
    if(isLiveReport){
      const downloadPayload = buildDownloadPayload(
        selectedReport,
        dateRangeType,
        fromDate,
        toDate,
        period,
        year,
        format,
        filtersMap,
        salesDrillDownPath,
        geoDrillDownPath
      );
      const blob = await downloadLiveReport(downloadPayload);
      triggerBrowserDownload(blob, selectedReport.reportName, format);
    } else {
      const downloadPayload = buildSnapshotDownloadPayload(
        selectedReport,
        dateRangeType,
        fromDate,
        toDate,
        period,
        year,
        format,
        filtersMap,
        salesDrillDownPath,
        geoDrillDownPath
      );
      const blob = await downloadSnapshotReport(downloadPayload);
      triggerBrowserDownload(blob, selectedReport.reportName, format);
    } 
    return {
      success: true,
      message: `${selectedReport.name} downloaded successfully!`
    };

  } catch (error: any) {
    console.error("Error downloading report:", error);
    return {
      success: false,
      message: "Failed to download report. Please try again.",
      error
    };
  }
}

/**
 * Load distributor options based on geographical hierarchy selections
 */
export async function loadDistributorOptionsForHierarchy(
  selectedReport: newReportConfig | null,
  filters: Record<string, string[]>
): Promise<string[]> {
  if (!selectedReport) return [];

  try {
    let users: string[] = [];
    
    if (selectedReport.geographicalHierarchyFilter?.enabled) {
      const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
      const selectedLevel = filters[levelKey]?.[0];
      const selectedValue = filters[selectedLevel]?.[0];
      
      if (selectedLevel && selectedValue) {
        const locationUsers = await fetchLocationUsers(selectedLevel, selectedValue, 'supplier');
        users = locationUsers;
      }
    }
    
    return users;
  } catch (error) {
    console.error("Error loading distributor options:", error);
    return [];
  }
}

/**
 * Build PDF report payload for batch invoice PDF
 */
export interface PDFReportDownloadRequest {
  attributes: {
    name: string;
    format: string;
    loggedInUserName?: string;
    fromDate: string;
    toDate: string;
    filters: {
      map: Record<string, string[]>;
    };
  };
  lob: string;
}

/**
 * Convert Dayjs to date string format for PDF report
 */
const convertDayjsToDateString = (date: Dayjs): string => {
  if (!date || !date.isValid()) {
    return new Date().toISOString().split('T')[0] + ' 00:00:00';
  }
  
  const year = date.year();
  const month = String(date.month() + 1).padStart(2, '0');
  const day = String(date.date()).padStart(2, '0');
  
  return `${year}-${month}-${day} 00:00:00`;
};

/**
 * Build PDF report download payload
 */
export function buildPDFReportPayload(
  selectedReport: newReportConfig,
  fromDate: Dayjs,
  toDate: Dayjs,
  distributorCodes: string[],
  isDistributorView: boolean
): PDFReportDownloadRequest {
  const lob = getLob();
  
  const payload: PDFReportDownloadRequest = {
    attributes: {
      name: "StandardInvoice",
      format: "pdf",
      fromDate: convertDayjsToDateString(fromDate),
      toDate: convertDayjsToDateString(toDate),
      filters: {
        map: {}
      }
    },
    lob: lob
  };

  // Only add loggedInUserName if isDistributorView is true
  if (isDistributorView) {
    const authContext = JSON.parse(localStorage.authContext || '{}');
    const loggedInUser = authContext?.user?.loginId;
    if (loggedInUser) {
      payload.attributes.loggedInUserName = loggedInUser;
    }
  }

  // Add distributor codes to filters
  if (distributorCodes.length > 0) {
    payload.attributes.filters.map.distributor_code = distributorCodes;
  }

  return payload;
}


export async function downloadPDFReportWithStatus(
  selectedReport: newReportConfig,
  filters: Record<string, string[]>,
  fromDate: Dayjs,
  toDate: Dayjs,
  primaryFilter: "sales" | "geographical" | "distributor" | null,
  salesDrillDownPath?: Array<{ level: string; value: string }>,
  geoDrillDownPath?: Array<{ level: string; value: string }>
): Promise<{
  success: boolean;
  message: string;
  error?: any;
}> {
  try {
    // Validate date range
    if (fromDate && toDate) {
      if (toDate.isBefore(fromDate)) {
        return {
          success: false,
          message: "To Date should be greater than from date."
        };
      }
    }

    const distributorCodes = await collectDistributorCodes(
      selectedReport,
      filters,
      primaryFilter,
      salesDrillDownPath,
      geoDrillDownPath
    );

    // Build the PDF report payload
    const isDistributorView = selectedReport.isDistributorView || false;
    const payload = buildPDFReportPayload(
      selectedReport,
      fromDate,
      toDate,
      distributorCodes,
      isDistributorView
    );

    // Create the task
    const createTaskResponse = await ChannelkartNetworkPost(
      "/tasks/types/batchInvoicePdf/execute",
      payload
    );

    const taskId = createTaskResponse?.data?.id;

    if (!taskId) {
      return {
        success: false,
        message: "Failed to create download task. Please try again."
      };
    }

    // Poll for task completion
    const fileKeys = await new Promise<string[]>((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const taskStatusResponse = await ChannelkartNetworkGet(`/tasks/${taskId}`);

          const status = taskStatusResponse?.data?.features?.[0]?.status ?? "";
          const fileKeys = taskStatusResponse?.data?.features?.[0]?.attributes?.fileKeys ?? [];

          if (status?.toLowerCase() === "success") {
            if (!fileKeys || fileKeys.length === 0 || fileKeys[0] === "") {
              clearInterval(interval);
              reject(new Error("Failed to download report. Please try again."));
              return;
            }
            clearInterval(interval);
            resolve(fileKeys);
          } else if (status?.toLowerCase() === "failure") {
            clearInterval(interval);
            reject(new Error("Failed to download report. Please try again."));
          }
        } catch (err) {
          clearInterval(interval);
          reject(err);
          console.error("Error polling task status:", err);
        }
      }, 1000);
    });

    // Download all file keys
    for (const fileKey of fileKeys) {
      await fetchAndDownloadReport(fileKey);
    }

    return {
      success: true,
      message: `${selectedReport.name} downloaded successfully!`
    };

  } catch (error: any) {
    console.error("Error downloading PDF report:", error);
    return {
      success: false,
      message: error.message || "Failed to download PDF report. Please try again.",
      error
    };
  }
}

